import {
  DEFAULT_CATALOG_ITEMS_PER_PAGE,
  DEFAULT_CATALOG_PAGE,
  MAX_SUGGESTED_ITEMS_PER_PAGE,
  SUGGESTION_SECTION_TITLES,
} from 'core/constants/catalogue.constants';
import { ImageRoutes } from 'core/constants/image-routes';
import { ESuggestedProductType } from 'core/model/enums/catalog.enum';
import { ICatalogueProducts, ISuggestionSection } from 'core/model/interfaces/catalogues.interface';
import {
  IPaginatedProducts,
  IPaginatedProductsParams,
  IProduct,
  IProductDetail,
} from 'core/model/interfaces/product.interface';
import {
  IGetCatalogProductsParams,
  IGetListProductsDTO,
  INavigationCatalogDTO,
  IProductDetailDTO,
  IProductDTO,
  TGetSuggestionsDTO,
} from './catalogs.dto';
import { getUnitMeasureDetailFromDTO, getUnitMeasureFromDTO } from './catalogs.utils';

export const productDTOToIProduct = (product: IProductDTO): IProduct => {
  const { brand, name, imageUrl, id, sourceId, stock, taxRate, ean, category, family, provider, distributor } = product;

  return {
    brand: brand.name,
    currency: {
      id: product.price.currency.code,
      symbol: product.price.currency.symbol,
    },
    name,
    eanCode: ean,
    sku: id,
    stock,
    sourceId: Number(sourceId),
    taxRate,
    unitMeasures: [getUnitMeasureFromDTO(product)],
    categoryName: category.name,
    subcategoryName: family.name,
    providerDisplayName: provider.name,
    distributorName: distributor.name,
    distributorId: distributor.id,
    categoryId: Number(category.id),
    subcategoryId: Number(family.id),
    image: imageUrl || ImageRoutes.CATALOGUE_DEFAULT_PRODUCT,
    octagonsImageUrl: product.octagonsImageUrl || '',
  };
};

export const catalogProductosParamsToBody = (params: IPaginatedProductsParams): IGetCatalogProductsParams => {
  const { itemsIds, parentId } = params;
  const pageSettings = { page: DEFAULT_CATALOG_PAGE, itemsPerPage: DEFAULT_CATALOG_ITEMS_PER_PAGE };
  if (parentId) {
    return { ...pageSettings, navigationSubItemIds: itemsIds };
  }
  return { ...pageSettings, navigationItemIds: itemsIds };
};

export const navigationCatalogToCatalogueProducts = (
  catalog: INavigationCatalogDTO,
  treeItemType: 'subcategory' | 'category',
): ICatalogueProducts => ({
  [treeItemType]: {
    guid: catalog.id.toString(),
    name: catalog.name,
  },
  products: mapProductDTOsToIProducts(catalog.products),
  pagination: catalog.pagination,
});

export const listProductsToPaginatedProducts = ({ pagination, products }: IGetListProductsDTO): IPaginatedProducts => ({
  products: mapProductDTOsToIProducts(products),
  pagination,
});

export const catalogDetailResponseToProduct = (product: IProductDetailDTO): IProductDetail => {
  const {
    brand,
    name,
    description,
    imageUrl,
    id,
    sourceId,
    taxRate,
    presentations,
    ean,
    category,
    family,
    provider,
    distributor,
  } = product;
  const productDetail: IProductDetail = {
    brand: brand.name,
    currency: {
      id: product.currency.code,
      symbol: product.currency.symbol,
    },
    octagonsImageUrl: product.octagonsImageUrl || '',
    name,
    eanCode: ean,
    sku: id,
    sourceId: Number(sourceId),
    taxRate,
    description,
    categoryName: category.name,
    subcategoryName: family.name,
    providerDisplayName: provider.name,
    distributorName: distributor.name,
    categoryId: Number(category.id),
    subcategoryId: Number(family.id),
    image: imageUrl || ImageRoutes.CATALOGUE_DEFAULT_PRODUCT,
    unitMeasures: presentations.map(presentation => getUnitMeasureDetailFromDTO(presentation, presentations.length)),
    distributor: {
      id: product.distributor.id,
      name: product.distributor.name,
      document: product.distributor.document,
    },
  };

  return productDetail;
};

export const mapProductDTOsToIProducts = (products: Array<IProductDTO>): Array<IProduct> => {
  const mappedProducts: Array<IProduct> = [];

  // NOSONAR
  products.forEach(product => {
    try {
      const productMapped = productDTOToIProduct(product);
      mappedProducts.push(productMapped);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
    }
  });

  return mappedProducts;
};

export const getSuggestionSections = (
  suggestions: TGetSuggestionsDTO,
  suggestionTypeId: ESuggestedProductType,
): ISuggestionSection => {
  const { products, pagination } = suggestions;
  const quantity = Math.min(pagination.totalItems, MAX_SUGGESTED_ITEMS_PER_PAGE);

  return {
    title: SUGGESTION_SECTION_TITLES[suggestionTypeId],
    type: suggestionTypeId,
    products: mapProductDTOsToIProducts(products),
    hasMoreProducts: pagination.totalPages > 1,
    quantity,
  };
};
